import React, { useState, useRef, useEffect } from 'react';

/**
 * COGSTable — reusable table with:
 *   - sticky header row
 *   - sticky first column (product name)
 *   - auto-calculated COGS = sum of all cost columns
 *   - inline editing for master-bag, small-bag costs and seeds cost-per-lb
 *
 * Props:
 *   columns    — array of column definitions
 *   data       — array of row objects
 *   hourlyRate — $/hr for labor columns
 *   toolsCost  — fixed tools cost
 *   lostPct    — lost % added on top of subtotal
 *   onSaveCost — async (type, id, value) => void
 */

function computeItemCOGS(item, hourlyRate, lostPct) {
  const sub = (item.bag_cost ?? 0) + (item.seeds ?? 0) + (item.labor_hours ?? 0) * (hourlyRate ?? 0);
  return sub * (Number(item.qty) || 1) * (1 + (lostPct ?? 0) / 100);
}

// ── Inline editable cost cell ─────────────────────────────────────────────────
const EditableCostCell = ({ displayValue, rawValue, onSave, fmt, children }) => {
  const [editing, setEditing] = useState(false);
  const [input,   setInput]   = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      setInput(rawValue != null ? String(rawValue) : '');
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [editing, rawValue]);

  const commit = () => {
    const v = parseFloat(input);
    if (!isNaN(v) && v >= 0 && v !== rawValue) onSave(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.0001"
        min="0"
        value={input}
        onChange={e => setInput(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ width: '80px', fontSize: '0.85em', padding: '1px 4px' }}
      />
    );
  }

  return (
    <span
      className="cogs-editable"
      title="Click to edit"
      onClick={() => setEditing(true)}
      style={{ cursor: 'pointer', borderBottom: '1px dashed #94a3b8' }}
    >
      {children ?? fmt(displayValue)}
    </span>
  );
};

// ── Seeds cost cell — shows computed cost but edits cost-per-lb ───────────────
const SeedsCostCell = ({ row, fmt, onSaveCost }) => {
  const seedsID     = row.seeds_id;
  const costPerLb   = row.seeds_cost_per_lb;
  const seedsCost   = row.seeds;   // already computed: costPerLb * lbPerPacket

  if (!seedsID) return <span>{fmt(seedsCost)}</span>;

  return (
    <EditableCostCell
      displayValue={seedsCost}
      rawValue={costPerLb}
      fmt={fmt}
      onSave={v => onSaveCost('seeds', seedsID, v)}
    >
      <span title={`$${costPerLb != null ? Number(costPerLb).toFixed(4) : '—'}/lb`}>
        {fmt(seedsCost)}
      </span>
    </EditableCostCell>
  );
};

// ── Fertilizer raw material cost cell — shows computed cost, edits cost-per-lb
const FertCostCell = ({ row, fmt, onSaveCost }) => {
  const rawMaterialID = row.fert_raw_material_id;
  const costPerLb     = row.fert_cost_per_lb;
  const fertCost      = row.fert_cost;  // already computed: costPerLb * volumeLb

  if (!rawMaterialID) return <span>{fmt(fertCost)}</span>;

  return (
    <EditableCostCell
      displayValue={fertCost}
      rawValue={costPerLb}
      fmt={fmt}
      onSave={v => onSaveCost('fert-material', rawMaterialID, v)}
    >
      <span title={`$${costPerLb != null ? Number(costPerLb).toFixed(4) : '—'}/lb × ${row.volume_lb ?? '?'} lb`}>
        {fmt(fertCost)}
      </span>
    </EditableCostCell>
  );
};

// ── Other order expenses cell for seeds — edits per-lb, shows computed cost ───
const SeedsOtherExpCell = ({ row, fmt, onSaveCost }) => {
  const seedsID = row.seeds_id;
  const perLb   = row.seeds_other_expenses_per_lb;
  const cost    = row.seeds_other_expenses;

  if (!seedsID) return <span style={{ color: '#94a3b8' }}>—</span>;

  return (
    <EditableCostCell
      displayValue={cost || 0}
      rawValue={perLb}
      fmt={fmt}
      onSave={v => onSaveCost('seeds-other-expenses', seedsID, v)}
    >
      <span title={`Other order expenses: $${perLb != null ? Number(perLb).toFixed(4) : '0'}/lb (Packaging + Freight & Handling + Miscellaneous)`}>
        {(!cost || cost === 0) ? <span style={{ color: '#94a3b8' }}>—</span> : fmt(cost)}
      </span>
    </EditableCostCell>
  );
};

// ── Other order expenses cell for ready products — edits per-pcs, shows computed
const RPOtherExpCell = ({ row, fmt, onSaveCost }) => {
  const perPcs = row.other_expenses_per_pcs;
  const cost   = row.other_expenses;

  return (
    <EditableCostCell
      displayValue={cost || 0}
      rawValue={perPcs}
      fmt={fmt}
      onSave={v => onSaveCost('ready-product-other-expenses', row.sku, v)}
    >
      <span title={`Other order expenses: $${perPcs != null ? Number(perPcs).toFixed(4) : '0'}/pcs (Packaging + Freight & Handling + Miscellaneous)`}>
        {(!cost || cost === 0) ? <span style={{ color: '#94a3b8' }}>—</span> : fmt(cost)}
      </span>
    </EditableCostCell>
  );
};

// ── Other order expenses cell for fertilizers — edits per-lb, shows computed ─
const FertOtherExpCell = ({ row, fmt, onSaveCost }) => {
  const rawMaterialID = row.fert_raw_material_id;
  const perLb         = row.fert_other_expenses_per_lb;
  const cost          = row.fert_other_expenses;

  if (!rawMaterialID) return <span style={{ color: '#94a3b8' }}>—</span>;

  return (
    <EditableCostCell
      displayValue={cost || 0}
      rawValue={perLb}
      fmt={fmt}
      onSave={v => onSaveCost('fert-other-expenses', rawMaterialID, v)}
    >
      <span title={`Other order expenses: $${perLb != null ? Number(perLb).toFixed(4) : '0'}/lb × ${row.volume_lb ?? '?'} lb (Packaging + Freight & Handling + Miscellaneous)`}>
        {(!cost || cost === 0) ? <span style={{ color: '#94a3b8' }}>—</span> : fmt(cost)}
      </span>
    </EditableCostCell>
  );
};

// ── Fertilizer labor cell — direct cost per package, stored in browser only ───
const FertLaborCell = ({ row, fmt, fertLabor, onSetFertLabor }) => {
  const sku       = row.sku;
  const costPerPkg = fertLabor?.[sku] ?? 0;

  return (
    <EditableCostCell
      displayValue={costPerPkg}
      rawValue={costPerPkg}
      fmt={fmt}
      onSave={v => onSetFertLabor(sku, v)}
    >
      <span title="Labor cost per package (browser only)">
        {costPerPkg > 0 ? fmt(costPerPkg) : <span style={{ color: '#94a3b8' }}>Set cost</span>}
      </span>
    </EditableCostCell>
  );
};

// ── Shared hook: compute fixed popover position from anchor button ────────────
function useFixedPopoverStyle(anchorRef) {
  const [style, setStyle] = useState({ top: 0, left: 0, opacity: 0 });

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const minWidth = 360;
    let left = rect.left;
    if (left + minWidth > window.innerWidth - 12) {
      left = Math.max(8, window.innerWidth - minWidth - 12);
    }
    setStyle({ top: rect.bottom + 6, left, opacity: 1 });
  }, [anchorRef]);

  return style;
}

// ── Breakdown popover ─────────────────────────────────────────────────────────
const BreakdownPopover = ({ items, anchorRef, onClose, hourlyRate, lostPct, fmt, onSaveCost }) => {
  const popRef = useRef(null);
  const posStyle = useFixedPopoverStyle(anchorRef);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  const itemsWithCOGS = items.map(item => ({
    ...item,
    cogs: computeItemCOGS(item, hourlyRate, lostPct),
    estimated: item.bag_cost_estimated || item.labor_hours_estimated,
  }));
  const total = itemsWithCOGS.reduce((s, i) => s + i.cogs, 0);

  return (
    <div className="cogs-breakdown-popover" ref={popRef} style={posStyle}>
      <div className="cogs-breakdown-title">Small Bags Breakdown</div>
      <table className="cogs-breakdown-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Name</th>
            <th>Qty</th>
            <th title="Click bag cost or seeds cost to edit">Bag Cost</th>
            <th title="Seeds cost per lb — click to edit">Seeds $/lb</th>
            <th>COGS</th>
          </tr>
        </thead>
        <tbody>
          {itemsWithCOGS.map(item => (
            <tr key={item.sku}>
              <td>{item.sku}</td>
              <td>{item.estimated ? <em style={{ color: '#a05a00' }}>{item.name} ~</em> : item.name}</td>
              <td>{Number(item.qty) || 1}</td>
              <td>
                <EditableCostCell
                  displayValue={item.bag_cost}
                  rawValue={item.bag_cost}
                  fmt={fmt}
                  onSave={v => onSaveCost('small-bag', item.sku, v)}
                />
              </td>
              <td>
                {item.seeds_id ? (
                  <EditableCostCell
                    displayValue={item.seeds_cost_per_lb}
                    rawValue={item.seeds_cost_per_lb}
                    fmt={fmt}
                    onSave={v => onSaveCost('seeds', item.seeds_id, v)}
                  />
                ) : '—'}
              </td>
              <td style={item.estimated ? { color: '#a05a00', fontStyle: 'italic' } : {}}>
                {item.estimated ? `~${fmt(item.cogs)}` : fmt(item.cogs)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5}><strong>Total</strong></td>
            <td><strong>{fmt(total)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const BreakdownCell = ({ value, items, fmt, hourlyRate, lostPct, onSaveCost }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  if (!items || items.length === 0) return <span>{fmt(value)}</span>;

  return (
    <span className="cogs-breakdown-wrapper">
      <span>{fmt(value)}</span>
      <button
        ref={btnRef}
        className="cogs-breakdown-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title="View breakdown"
      >
        <i className="fas fa-list-ul"></i>
      </button>
      {open && (
        <BreakdownPopover
          items={items}
          anchorRef={btnRef}
          onClose={() => setOpen(false)}
          hourlyRate={hourlyRate}
          lostPct={lostPct}
          fmt={fmt}
          onSaveCost={onSaveCost}
        />
      )}
    </span>
  );
};

// ── Kit breakdown popover ─────────────────────────────────────────────────────
const KitBreakdownPopover = ({ items, anchorRef, onClose, fmt }) => {
  const popRef = useRef(null);
  const posStyle = useFixedPopoverStyle(anchorRef);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  const total = (items ?? []).reduce((s, i) => s + Number(i.line_cost ?? 0), 0);

  return (
    <div className="cogs-breakdown-popover" ref={popRef} style={posStyle}>
      <div className="cogs-breakdown-title">Kit Components</div>
      <table className="cogs-breakdown-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit Cost</th>
            <th>Line Cost</th>
          </tr>
        </thead>
        <tbody>
          {(items ?? []).map((item, idx) => (
            <tr key={idx} style={item.missing ? { color: '#ef4444' } : {}}>
              <td>{item.type_label}</td>
              <td>
                {item.missing
                  ? <span title="Cost not available">⚠ {item.display_name || item.component_ref}</span>
                  : (item.display_name || item.component_ref)}
              </td>
              <td>{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
              <td>{item.missing ? '—' : fmt(item.unit_cost)}</td>
              <td>{item.missing ? '—' : fmt(item.line_cost)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}><strong>Total</strong></td>
            <td><strong>{fmt(total)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const KitBreakdownCell = ({ row, fmt }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const items     = row.components_breakdown ?? [];
  const value     = Number(row.components_cost ?? 0);
  const hasMissing = row.has_missing_costs;

  return (
    <span className="cogs-breakdown-wrapper">
      <span style={hasMissing ? { color: '#ef4444' } : {}}>
        {hasMissing && <span title="Some component costs are missing">⚠ </span>}
        {fmt(value)}
      </span>
      {items.length > 0 && (
        <button
          ref={btnRef}
          className="cogs-breakdown-btn"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          title="View breakdown"
        >
          <i className="fas fa-list-ul"></i>
        </button>
      )}
      {open && (
        <KitBreakdownPopover
          items={items}
          anchorRef={btnRef}
          onClose={() => setOpen(false)}
          fmt={fmt}
        />
      )}
    </span>
  );
};

// ── Main table ────────────────────────────────────────────────────────────────
const COGSTable = ({ columns, data, hourlyRate, toolsCost, lostPct, onSaveCost, fertLabor, onSetFertLabor, businessExpPerUnit }) => {
  const fmt = (val) => {
    if (val == null) return '—';
    const n = Number(val);
    const fixed = n.toFixed(5).replace(/\.?0+$/, '');
    return `$${fixed}`;
  };

  const getSubtotal = (row) =>
    columns
      .filter(c => c.isCost && !c.isCalculated && !c.isLost)
      .reduce((sum, c) => {
        if (c.isFixedTools)      return sum + (toolsCost ?? 0);
        if (c.isLaborEditable)   return sum + (fertLabor?.[row.sku] ?? 0);
        if (c.isLabor)           return sum + (row[c.key] ?? 0) * hourlyRate;
        if (c.isFertCost)        return sum + Number(row[c.key] ?? 0);
        if (c.isSeedsOtherExp)   return sum + Number(row.seeds_other_expenses ?? 0);
        if (c.isFertOtherExp)    return sum + Number(row.fert_other_expenses ?? 0);
        if (c.isRPOtherExp)      return sum + Number(row.other_expenses ?? 0);
        if (c.isKitComponents)   return sum + Number(row.components_cost ?? 0);
        if (c.hasBreakdown) {
          const items = row[c.hasBreakdown] ?? [];
          return sum + items.reduce((s, item) => s + computeItemCOGS(item, hourlyRate, lostPct), 0);
        }
        return sum + Number(row[c.key] ?? 0);
      }, 0);

  const getCellValue = (row, col) => {
    if (col.isLost)          return getSubtotal(row) * (lostPct ?? 0) / 100;
    if (col.isCalculated)    { const sub = getSubtotal(row); return sub + sub * (lostPct ?? 0) / 100; }
    if (col.isFixedTools)    return toolsCost ?? 0;
    if (col.isLaborEditable) return fertLabor?.[row.sku] ?? 0;
    if (col.isLabor)         return (row[col.key] ?? 0) * hourlyRate;
    if (col.isSeedsOtherExp) return row.seeds_other_expenses ?? 0;
    if (col.isFertOtherExp)  return row.fert_other_expenses ?? 0;
    if (col.isRPOtherExp)    return row.other_expenses ?? 0;
    if (col.isKitComponents) return Number(row.components_cost ?? 0);
    if (col.hasBreakdown) {
      const items = row[col.hasBreakdown] ?? [];
      return items.reduce((s, item) => s + computeItemCOGS(item, hourlyRate, lostPct), 0);
    }
    return row[col.key] ?? null;
  };

  const firstCol = columns[0];
  const restCols = columns.slice(1);

  return (
    <div className="cogs-table-wrapper">
      <table className="cogs-table">
        <thead>
          <tr>
            <th className="cogs-th cogs-th--sticky-first">{firstCol.label}</th>
            {restCols.map(col => (
              <th
                key={col.key}
                className={`cogs-th${col.isCalculated ? ' cogs-th--cogs' : ''}${col.isLost ? ' cogs-th--lost' : ''}`}
                title={col.tooltip || undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td className="cogs-td cogs-td--sticky-first cogs-empty" colSpan={columns.length}>
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr key={row.sku ?? idx} className={idx % 2 === 1 ? 'cogs-tr--alt' : ''}>
                <td className="cogs-td cogs-td--sticky-first">
                  {firstCol.isName
                    ? `${row.brandID ?? ''} — ${row[firstCol.key] ?? '—'}`
                    : (row[firstCol.key] ?? '—')}
                </td>
                {restCols.map(col => {
                  const val = getCellValue(row, col);
                  const isCostLike  = col.isCost || col.isCalculated || col.isLost;
                  const isEstimated = col.estimatedFlag && row[col.estimatedFlag];

                  return (
                    <td
                      key={col.key}
                      className={`cogs-td${col.isCalculated ? ' cogs-td--cogs' : ''}${col.isLost ? ' cogs-td--lost' : ''}${isEstimated ? ' cogs-td--estimated' : ''}`}
                    >
                      {col.isKitComponents ? (
                        <KitBreakdownCell row={row} fmt={fmt} />
                      ) : col.hasBreakdown ? (
                        <BreakdownCell
                          value={val}
                          items={row[col.hasBreakdown]}
                          fmt={fmt}
                          hourlyRate={hourlyRate}
                          lostPct={lostPct}
                          onSaveCost={onSaveCost}
                        />
                      ) : col.isSeedsOtherExp ? (
                        <SeedsOtherExpCell row={row} fmt={fmt} onSaveCost={onSaveCost} />
                      ) : col.isFertOtherExp ? (
                        <FertOtherExpCell row={row} fmt={fmt} onSaveCost={onSaveCost} />
                      ) : col.isRPOtherExp ? (
                        <RPOtherExpCell row={row} fmt={fmt} onSaveCost={onSaveCost} />
                      ) : col.isFertCost ? (
                        <FertCostCell row={row} fmt={fmt} onSaveCost={onSaveCost} />
                      ) : col.isLaborEditable ? (
                        <FertLaborCell
                          row={row}
                          fmt={fmt}
                          fertLabor={fertLabor}
                          onSetFertLabor={onSetFertLabor}
                        />
                      ) : col.isSeedsCost ? (
                        <SeedsCostCell row={row} fmt={fmt} onSaveCost={onSaveCost} />
                      ) : col.editType ? (
                        <EditableCostCell
                          displayValue={val}
                          rawValue={val}
                          fmt={fmt}
                          onSave={v => onSaveCost(col.editType, row[col.editKey ?? 'sku'], v)}
                        >
                          {isEstimated
                            ? <span title="Brand average estimate">~{fmt(val)}</span>
                            : undefined}
                        </EditableCostCell>
                      ) : isCostLike ? (
                        isEstimated
                          ? <span title="Brand average estimate">~{fmt(val)}</span>
                          : col.isLabor && col.isLaborShiftBased && row.produce_per_shift != null
                            ? <span title={
                                `Qty per shift = ${row.produce_per_shift}\n` +
                                `Shift = 8 hours\n` +
                                `Hour rate = $${hourlyRate}/hr\n` +
                                `Shift / Qty per shift = ${Number(row[col.key] ?? 0).toFixed(4)} hrs/unit\n` +
                                `Shift / Qty per shift × Hour rate = ${fmt(val)}`
                              }>{fmt(val)}</span>
                          : col.isLabor
                            ? <span title={`${row[col.key] ?? 0} hrs × $${hourlyRate}/hr`}>{fmt(val)}</span>
                            : fmt(val)
                      ) : (val ?? '—')}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default COGSTable;
